'use client';
import { useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';

import { useGrokSummary } from '@/services/query';
import { handleApiError } from '@/utils';

interface MarketContextProps {
  slug: string;
}

export const MarketContext = ({ slug }: MarketContextProps) => {
  const [data, setData] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const { mutate: generateSummary, isPending: loading } = useGrokSummary();

  const handleGenerate = () => {
    setExpanded(true);
    generateSummary(
      { prompt: slug, locale: 'en' },
      {
        onSuccess: (response) => {
          if (response.success) {
            setData(response.data);
          }
        },
        onError: (error) => {
          handleApiError(error);
        },
      },
    );
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow: 'none',
        mb: 2,
      }}
    >
      <CardContent sx={{ p: '20px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            sx={{
              fontSize: expanded ? '14px' : '16px',
              fontWeight: expanded ? 500 : 700,
              color: expanded ? 'text.secondary' : 'text.primary',
              transition: 'all 0.2s',
            }}
          >
            Market Context
          </Typography>
          {!expanded ? (
            <Button
              variant="text"
              onClick={handleGenerate}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'primary.main',
                p: 0,
                minWidth: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Generate
            </Button>
          ) : (
            <IconButton onClick={toggleExpand} size="small" sx={{ p: 0 }}>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: '20px' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: '20px' }} />
              )}
            </IconButton>
          )}
        </Box>

        {expanded && (
          <Box sx={{ mt: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <CircularProgress size={16} thickness={6} sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                  Getting sources...
                </Typography>
              </Box>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    fontSize: '14px',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                  }}
                >
                  {data}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 2,
                    textAlign: 'right',
                    color: 'text.secondary',
                    fontSize: '11px',
                  }}
                >
                  Results are experimental.
                </Typography>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketContext;
